import { useField } from 'formik';

import { Accordion } from '../../cdl/Accordion/Accordion';
import { Label } from '../../cdl/Label/Label';
import { Radio, RadioGroup, RadioGroupItem } from '../../cdl/RadioGroup/RadioGroup';
import { Tag } from '../../cdl/Tag/Tag';
import { translate } from '../../common/helpers/translate.helper';
import { Box } from '../../common/ui/Box';

interface FeatureFlagStateTagProps {
    isManualOverride: boolean;
    currentValue: boolean;
    launchDarklyValue: boolean;
}

const FeatureFlagStateTag = ({ isManualOverride, currentValue, launchDarklyValue }: FeatureFlagStateTagProps) => {
    if (isManualOverride) {
        return (
            <Tag variant={currentValue ? 'positive' : 'negative'} emphasis>
                {currentValue ? translate('devTools.featureFlags.true') : translate('devTools.featureFlags.false')}
            </Tag>
        );
    }

    return (
        <Tag variant={launchDarklyValue ? 'positive' : 'negative'}>
            {translate('devTools.featureFlags.launchDarkly')}
        </Tag>
    );
};

interface FeatureFlagAccordionItemProps {
    name: string;
    launchDarklyValue: boolean;
    storedValue: boolean | null;
}

const LAUNCHDARKLY_VALUE = 'launchdarkly';
const TRUE_VALUE = 'true';
const FALSE_VALUE = 'false';

export const FeatureFlagAccordionItem = ({ name, launchDarklyValue, storedValue }: FeatureFlagAccordionItemProps) => {
    const [field, , helpers] = useField(name);

    const isStoredOverride = storedValue !== null;
    const storedCurrentValue = isStoredOverride ? storedValue : launchDarklyValue;

    const getRadioValue = (): string => {
        if (field.value === null) {
            return LAUNCHDARKLY_VALUE;
        }
        return field.value ? TRUE_VALUE : FALSE_VALUE;
    };

    const handleValueChange = (value: string) => {
        if (value === LAUNCHDARKLY_VALUE) {
            helpers.setValue(null);
        } else if (value === TRUE_VALUE) {
            helpers.setValue(true);
        } else {
            helpers.setValue(false);
        }
    };

    return (
        <Accordion.Item
            id={name}
            title={name}
            summary={
                <Box display="flex" alignItems="center" justifyContent="flex-end" gap={4} width="100%">
                    <FeatureFlagStateTag
                        isManualOverride={isStoredOverride}
                        currentValue={storedCurrentValue}
                        launchDarklyValue={launchDarklyValue}
                    />
                </Box>
            }
        >
            <Box paddingLeft={8}>
                <RadioGroup value={getRadioValue()} onValueChange={handleValueChange}>
                    <RadioGroupItem>
                        <Radio value={LAUNCHDARKLY_VALUE} />
                        <Label>
                            {translate('devTools.featureFlags.launchDarklyWithValue', {
                                value: launchDarklyValue.toString(),
                            })}
                        </Label>
                    </RadioGroupItem>
                    <RadioGroupItem>
                        <Radio value={TRUE_VALUE} />
                        <Label>{translate('devTools.featureFlags.true')}</Label>
                    </RadioGroupItem>
                    <RadioGroupItem>
                        <Radio value={FALSE_VALUE} />
                        <Label>{translate('devTools.featureFlags.false')}</Label>
                    </RadioGroupItem>
                </RadioGroup>
            </Box>
        </Accordion.Item>
    );
};
